// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview Implements the disposable interface. The dispose method is used
 * to clean up references and resources. Implementation based on Google Closure
 * Library goog.Disposable class.
 * @link https://google.github.io/closure-library/api/goog.Disposable.html
 * @author artem.lit@sisense.com (Artem Lytvynov)
 * @copyright sisense@sisense.com (Sisense Ltd.)
 * @license Sisense License
 */

'use strict';

const utils = require('@sbi/core.utils');

/**
 * Calls {@code dispose} on the argument if it supports it. If obj is not an
 * object with a dispose( ) method, this is a no-op.
 * @param {*} obj The object to dispose of.
 * @returns {void}
 */
const dispose = function (obj) {
    if (obj && typeof obj.dispose === 'function') {
        obj.dispose();
    }
};

/**
 * Calls {@code dispose} on each member of the list that supports it. (If
 * the member is an ArrayLike, then {@code disposeAll()} will be called
 * recursively on each of its members.) If the member is not an object with
 * a {@code dispose()} method, then it is ignored.
 * @param {...*} args The list (array) of disposable entities.
 */
// eslint-disable-next-line
const disposeAll = function (...args) {
    // eslint-disable-next-line
        for (let i = 0, len = args.length; i < len; ++i) {
        // eslint-disable-next-line
            const disposable = args[i];
        if (utils.isArrayLike(disposable)) {
            disposeAll(...disposable);
        } else {
            dispose(disposable);
        }
    }
};

/**
 * Class that provides the basic implementation for disposable objects. If
 * your class holds one or more references to COM objects, DOM nodes, or
 * other disposable objects, it should extend this class.
 */
class Disposable {
    /**
     * @constructor
     */
    constructor() {
        /**
         * Whether the object has been disposed of.
         * @type {boolean}
         * @private
         */
        this.disposed_ = false;

        /**
         * If monitoring the Disposable instances is enabled, stores the
         * creation stack trace of the Disposable instance.
         * @type {string}
         */
        this.creationStack = '';

        if (Disposable.MONITORING_MODE !== Disposable.MonitoringMode.OFF) {
            if (Disposable.INCLUDE_STACK_ON_CREATION) {
                this.creationStack = new Error().stack;
            }
            Disposable.instances_[utils.getUid(this)] = this;
        }
    }

    /**
     * @returns {boolean} Whether the object has been disposed of.
     */
    isDisposed() {
        return this.disposed_;
    }

    /**
     * Disposes of the object. If the object hasn't already been disposed
     * of, calls {@link #disposeInternal}. Classes that extend
     * {@code Disposable} should override {@link #disposeInternal} in order
     * to delete references to COM objects, DOM nodes, and other disposable
     * objects.
     * @returns {void} Nothing.
     */
    dispose() {
        if (!this.disposed_) {
            // Set disposed_ to true first, in case during the chain of
            // disposal this gets disposed recursively.
            this.disposed_ = true;
            this.disposeInternal();
            if (Disposable.MONITORING_MODE
                !== Disposable.MonitoringMode.OFF) {
                const uid = utils.getUid(this);
                if (
                    Disposable.MONITORING_MODE === Disposable.MonitoringMode.PERMANENT
                    // eslint-disable-next-line
                    && !Disposable.instances_.hasOwnProperty(uid)
                ) {
                    throw new Error(`${this} did not call the Disposable base `
                        + 'constructor or was disposed of after a '
                        + 'clearUndisposedObjects call');
                }
                delete Disposable.instances_[uid];
            }
        }
    }

    /**
     * Deletes or nulls out any references to COM objects, DOM nodes, or
     * other disposable objects. Classes that extend {@code Disposable}
     * should override this method.
     * Not re-entrant. To avoid calling it twice, it must only be called from
     * the subclass' {@code disposeInternal} method. Everywhere else the
     * public {@code dispose} method must be used.
     * @returns {void}
     * @protected
     */
    disposeInternal() {
        if (this.onDisposeCallbacks_) {
            while (this.onDisposeCallbacks_.length) {
                this.onDisposeCallbacks_.shift()();
            }
        }
    }

    /**
     * Associates a disposable object with this object so that they will be
     * disposed together.
     * @param {Disposable} disposable that will be disposed when this object
     * is disposed.
     * @returns {void}
     */
    registerDisposable(disposable) {
        this.addOnDisposeCallback(dispose.bind(undefined, disposable));
    }

    /**
     * Invokes a callback function when this object is disposed. Callbacks
     * are invoked in the order in which they were added. If a callback is
     * added to an already disposed Disposable, it will be called
     * immediately.
     * @param {function(this:T):?} callback The callback function.
     * @param {T=} opt_scope An optional scope to call the callback in.
     * @template T
     * @returns {void}
     */
    addOnDisposeCallback(callback, opt_scope) {
        if (this.disposed_) {
            callback.call(opt_scope);
            return;
        }
        if (!this.onDisposeCallbacks_) {
            /**
             * Callbacks to invoke when this object is disposed.
             * @type {Array<!Function>}
             * @private
             */
            this.onDisposeCallbacks_ = [];
        }
        // eslint-disable-next-line
        this.onDisposeCallbacks_.push(opt_scope !== void 0 ? callback.bind(opt_scope) : callback);
    }
}

/**
 * @enum {number} Different monitoring modes for Disposable.
 */
Disposable.MonitoringMode = {

    /**
     * No monitoring.
     */
    OFF: 0,

    /**
     * Creating and disposing the Disposable instances is monitored.
     * All disposable objects need to call the {@code Disposable} base
     * constructor. The PERMANENT mode must be switched on before creating
     * any Disposable instances.
     */
    PERMANENT: 1,

    /**
     * INTERACTIVE mode can be switched on and off on the fly without
     * producing errors. It also doesn't warn if the disposable objects
     * don't call the {@code Disposable} base constructor.
     */
    INTERACTIVE: 2,
};

/**
 * @define {number} The monitoring mode of the Disposable instances. Default
 * is OFF. Switching on the monitoring is only recommended for debugging
 * because it has a significant impact on performance and memory usage.
 */
Disposable.MONITORING_MODE = 0;

/**
 * @define {boolean} Whether to attach creation stack to each created
 * disposable instance; This is only relevant for when MonitoringMode != OFF
 */
Disposable.INCLUDE_STACK_ON_CREATION = true;

/**
 * Maps the unique ID of every undisposed {@code Disposable} object to the
 * object itself.
 * @type {!Object<number, !Disposable>}
 */
Disposable.instances_ = { };

/**
 * @returns {!Array<!Disposable>} All {@code Disposable} objects that haven't
 * been disposed of.
 */
Disposable.getUndisposedObjects = function () {
    const ret = [];
    // eslint-disable-next-line
    for (const id in Disposable.instances_) {
        // eslint-disable-next-line
        if (Disposable.instances_.hasOwnProperty(id)) {
            ret.push(Disposable.instances_[Number(id)]);
        }
    }
    return ret;
};

/**
 * Clears the registry of undisposed objects but doesn't dispose of them.
 * @returns {void}
 */
Disposable.clearUndisposedObjects = function () {
    Disposable.instances_ = { };
};

/**
 * Returns True if we can verify the object is disposed.
 * Calls {@code isDisposed} on the argument if it supports it.  If obj
 * is not an object with an isDisposed() method, return false.
 * @param {*} obj The object to investigate.
 * @returns {boolean} True if we can verify the object is disposed.
 */
Disposable.isDisposed = function (obj) {
    if (obj && typeof obj.isDisposed === 'function') {
        return obj.isDisposed();
    }
    return false;
};


// Exports:
exports.Disposable = Disposable;
exports.disposeAll = disposeAll;
exports.dispose = dispose;
